import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  Chip,
  useTheme,
  alpha,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from "@mui/material";
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import {
  MdOutlineAccountBalanceWallet,
  MdCheckCircleOutline,
  MdOutlineDataUsage,
  MdPeopleOutline,
} from "react-icons/md";
import SidebarTopbar from "../../components/layouts/layout";

const BASE_URL = process.env.REACT_APP_BASE_URL;

// Colors for charts
const COLORS = ["#1e4de3", "#00C49F", "#FFBB28", "#FF8042", "#8884d8"];

export default function AdminPanel() {
  const theme = useTheme();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetailData, setUserDetailData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  const handleUserClick = async (user) => {
    setSelectedUser(user);
    setDialogOpen(true);
    setDetailLoading(true);
    setUserDetailData(null);
    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(`${BASE_URL}/credits/user/${user.id || user.user_id}/usage`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch user credit usage details");
      }

      const result = await response.json();
      setUserDetailData(result);
    } catch (err) {
      console.error(err);
    } finally {
      setDetailLoading(false);
    }
  };

  useEffect(() => {
    const fetchAdminData = async () => {
      try {
        const token = localStorage.getItem("authToken");
        const response = await fetch(`${BASE_URL}/credits/organization/usage`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!response.ok) {
          throw new Error("Failed to fetch admin data");
        }

        const result = await response.json();
        setData(result);
      } catch (err) {
        console.error(err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchAdminData();
  }, []);

  if (loading) {
    return (
      <SidebarTopbar>
        <Box display="flex" justifyContent="center" alignItems="center" height="100%">
          <CircularProgress style={{ color: "#1e4de3" }} />
        </Box>
      </SidebarTopbar>
    );
  }

  if (error || !data) {
    return (
      <SidebarTopbar>
        <Box sx={{ bgcolor: "#f8f9fa", display: "flex", justifyContent: "center", alignItems: "flex-start" }}>
          <Typography color="error" variant="h6">
            {error ? `Failed to load admin panel data: ${error}` : "No data available from backend."}
          </Typography>
        </Box>
      </SidebarTopbar>
    );
  }

  // Prepare chart data
  const pieData = Object.keys(data.usage_by_type || {})
    .filter((key) => ((data.usage_by_type || {})[key]?.credits_used || 0) > 0)
    .map((key) => ({
      name: key.toUpperCase(),
      value: (data.usage_by_type || {})[key]?.credits_used || 0,
    }));

  const barData = Object.keys(data.usage_by_type || {}).map((key) => ({
    name: key.toUpperCase(),
    count: (data.usage_by_type || {})[key]?.generated_count || 0,
  }));

  const orgName = data.organization_name || "your organization";

  const statCards = [
    {
      title: "Total Credits",
      value: data.total_credits || 0,
      icon: <MdOutlineAccountBalanceWallet size={20} color={alpha(theme.palette.primary.main, 0.8)} />,
      color: theme.palette.primary.main,
    },
    {
      title: "Available Credits",
      value: data.available_credits || 0,
      icon: <MdCheckCircleOutline size={20} color={alpha(theme.palette.success.main, 0.8)} />,
      color: theme.palette.success.main,
    },
    {
      title: "Credits Used",
      value: (data.total_credits || 0) - (data.available_credits || 0), // Calculate total used
      icon: <MdOutlineDataUsage size={20} color={alpha(theme.palette.warning.main, 0.8)} />,
      color: theme.palette.warning.main,
    },
    {
      title: "Total Users",
      value: data.users ? data.users.length : 0,
      icon: <MdPeopleOutline size={20} color={alpha(theme.palette.info.main, 0.8)} />,
      color: theme.palette.info.main,
    },
  ];

  return (
    <SidebarTopbar>
      <Box sx={{ bgcolor: "#f8f9fa", fontFamily: "'Inter', sans-serif", boxSizing: "border-box" }}>

        {/* Top Stat Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {statCards.map((stat, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: "12px",
                  boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
                  display: "flex",
                  alignItems: "center",
                  transition: "transform 0.3s ease",
                  "&:hover": { transform: "translateY(-5px)" },
                }}
              >
                <Box
                  sx={{
                    p: 1.2,
                    borderRadius: "50%",
                    bgcolor: alpha(stat.color, 0.1),
                    display: "flex",
                    mr: 3,
                  }}
                >
                  {stat.icon}
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ color: "#64748b", fontWeight: 600 }}>
                    {stat.title}
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: "#1e293b" }}>
                    {stat.value.toLocaleString()}
                  </Typography>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Charts Section */}
        <Grid container spacing={4} sx={{ mb: 4 }}>
          <Grid item xs={12} md={6}>
            <Card elevation={0} sx={{ p: 3, borderRadius: "12px", boxShadow: "0 4px 20px rgba(0,0,0,0.05)" }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: "#1e293b", mb: 3 }}>
                Credits Used by Content Type
              </Typography>
              <Box sx={{ height: 300, position: "relative" }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={80}
                      outerRadius={110}
                      paddingAngle={1}
                      dataKey="value"
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <RechartsTooltip
                      formatter={(value) => [`${value} Credits`, "Usage"]}
                      contentStyle={{ borderRadius: "8px", border: "none", boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }}
                    />
                    <Legend verticalAlign="bottom" height={36} />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card elevation={0} sx={{ p: 3, borderRadius: "12px", boxShadow: "0 4px 20px rgba(0,0,0,0.05)" }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: "#1e293b", mb: 3 }}>
                Generation Counts by Content Type
              </Typography>
              <Box sx={{ height: 300, position: "relative" }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={barData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                    <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: "#64748b" }} />
                    <YAxis axisLine={false} tickLine={false} tick={{ fill: "#64748b" }} />
                    <RechartsTooltip
                      cursor={{ fill: "transparent" }}
                      contentStyle={{ borderRadius: "8px", border: "none", boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }}
                    />
                    <Bar dataKey="count" fill="#1e4de3" radius={[6, 6, 0, 0]} maxBarSize={50} />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Card>
          </Grid>
        </Grid>

        {/* Users Data Table */}
        <Card elevation={0} sx={{ borderRadius: "12px", boxShadow: "0 4px 20px rgba(0,0,0,0.05)", overflow: "hidden" }}>
          <Box sx={{ p: 3, borderBottom: "1px solid #f1f5f9" }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: "#1e293b" }}>
              User Credit Usage Details
            </Typography>
          </Box>
          <TableContainer component={Paper} elevation={0}>
            <Table sx={{ minWidth: 650 }}>
              <TableHead sx={{ bgcolor: "#f8fafc" }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, color: "#475569" }}>User</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "#475569" }}>Email</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "#475569" }} align="right">
                    Credits Used
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(data.users || []).map((user) => {
                  const displayName = user.fullName || user.name || user.username;
                  const avatarLetter = displayName ? displayName[0].toUpperCase() : "?";

                  return (
                    <TableRow
                      key={user.id || user.user_id}
                      onClick={() => handleUserClick(user)}
                      sx={{
                        "&:last-child td, &:last-child th": { border: 0 },
                        cursor: "pointer",
                        transition: "background-color 0.2s ease",
                        "&:hover": {
                          bgcolor: alpha(theme.palette.primary.main, 0.04)
                        }
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <Avatar sx={{ bgcolor: "#e2e8f0", color: "#1e4de3", mr: 2, fontWeight: 600 }}>
                            {avatarLetter}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: "#1e293b" }}>
                              {displayName}
                            </Typography>
                            <Typography variant="caption" sx={{ color: "#94a3b8" }}>
                              @{user.username}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ color: "#64748b" }}>{user.email || "N/A"}</TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" sx={{ fontWeight: 600, color: "#1e4de3" }}>
                          {(user.credits_used || 0).toLocaleString()}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>

        {/* User Details Dialog */}
        <Dialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 4,
              boxShadow: "0 10px 30px rgba(0,0,0,0.1)",
              overflow: "hidden"
            }
          }}
        >
          <DialogTitle sx={{ p: 3, borderBottom: "1px solid #f1f5f9" }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: "#1e293b" }}>
                {selectedUser ? `${selectedUser.fullName || selectedUser.name || selectedUser.username}'s Generations` : "User Generations Details"}
              </Typography>
              <Typography variant="caption" component="div" sx={{ color: "#64748b", mt: 0.5 }}>
                {userDetailData ? (
                  <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: "#1e293b" }}>{userDetailData.generations?.length || 0}</Typography>
                      <Typography variant="body2">Assets Generated</Typography>
                    </Box>
                    <Box sx={{ width: 4, height: 4, borderRadius: "50%", bgcolor: "#cbd5e1" }} />
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: "#1e293b" }}>{userDetailData.total_credits_used?.toFixed(1) || 0}</Typography>
                      <Typography variant="body2">Credits Used</Typography>
                    </Box>

                    {userDetailData.generations?.length > 0 && (
                      <>
                        <Box sx={{ width: 4, height: 4, borderRadius: "50%", bgcolor: "#cbd5e1", display: { xs: 'none', sm: 'block' } }} />
                        <Box display="flex" gap={1} flexWrap="wrap">
                          {Object.entries(
                            userDetailData.generations.reduce((acc, gen) => {
                              acc[gen.GeneratedAssetType] = (acc[gen.GeneratedAssetType] || 0) + 1;
                              return acc;
                            }, {})
                          ).map(([type, count]) => {
                            // Match the chip colors used in the table
                            let chipColor = "#1e4de3"; // default primary
                            let chipBg = "rgba(30, 77, 227, 0.1)";

                            let displayType = `${type}s Generated`;
                            if (type === "PPT") displayType = "PPTs Generated";
                            else if (type === "Video") displayType = "Videos Generated";
                            else if (type === "Poster") displayType = "Posters Generated";
                            else if (type === "Graphs" || type === "Images") displayType = "Graphs Generated";
                            else if (type === "Carousel") displayType = "Carousels Generated";
                            else if (type === "Instapost") displayType = "Posts Generated";

                            if (type === "Video") {
                              chipColor = "#2e7d32"; // success
                              chipBg = "rgba(46, 125, 50, 0.1)";
                            } else if (type === "Poster") {
                              chipColor = "#ed6c02"; // warning
                              chipBg = "rgba(237, 108, 2, 0.1)";
                            } else if (type === "Images" || type === "Graphs") {
                              chipColor = "#0288d1"; // info
                              chipBg = "rgba(2, 136, 209, 0.1)";
                            } else if (type === "Carousel") {
                              chipColor = "#9c27b0"; // secondary
                              chipBg = "rgba(156, 39, 176, 0.1)";
                            } else if (type === "Instapost") {
                              chipColor = "#d32f2f"; // error
                              chipBg = "rgba(211, 47, 47, 0.1)";
                            }

                            return (
                              <Chip
                                key={type}
                                label={`${count} ${displayType}`}
                                size="small"
                                sx={{
                                  height: 22,
                                  fontSize: "0.7rem",
                                  fontWeight: 700,
                                  color: chipColor,
                                  bgcolor: chipBg
                                }}
                              />
                            );
                          })}
                        </Box>
                      </>
                    )}
                  </Box>
                ) : "Detailed usage record and asset breakdown"}
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ p: 0, minHeight: 250, maxHeight: "70vh", bgcolor: "#f8fafc", display: "flex", flexDirection: "column", overflow: "hidden" }}>
            {detailLoading ? (
              <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" height={250} gap={2}>
                <CircularProgress size={40} style={{ color: "#1e4de3" }} />
                <Typography variant="body2" sx={{ color: "#64748b" }}>Loading generations details...</Typography>
              </Box>
            ) : userDetailData && userDetailData.generations && userDetailData.generations.length > 0 ? (
              <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 0, border: "none", overflowX: "auto", overflowY: "auto", flexGrow: 1 }}>
                <Table sx={{ minWidth: 900 }}>
                  <TableHead sx={{ bgcolor: "#f1f5f9" }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, color: "#475569", width: 60 }} align="center">S.No</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: "#475569" }}>Publication Title</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: "#475569", width: 120 }}>Asset Type</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: "#475569" }}>Purpose</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: "#475569", width: 140 }}>Generated At</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: "#475569", width: 120 }} align="right">Base Credits</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: "#475569", width: 120 }} align="right">Regenerations</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: "#475569", width: 120 }} align="right">Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {userDetailData.generations.map((gen) => {
                      let chipColor = theme.palette.primary.main;
                      let chipBg = alpha(theme.palette.primary.main, 0.1);

                      if (gen.GeneratedAssetType === "Video") {
                        chipColor = theme.palette.success.main;
                        chipBg = alpha(theme.palette.success.main, 0.1);
                      } else if (gen.GeneratedAssetType === "Poster") {
                        chipColor = theme.palette.warning.main;
                        chipBg = alpha(theme.palette.warning.main, 0.1);
                      } else if (gen.GeneratedAssetType === "Images" || gen.GeneratedAssetType === "Graphs") {
                        chipColor = theme.palette.info.main;
                        chipBg = alpha(theme.palette.info.main, 0.1);
                      } else if (gen.GeneratedAssetType === "Carousel") {
                        chipColor = theme.palette.secondary.main;
                        chipBg = alpha(theme.palette.secondary.main, 0.1);
                      } else if (gen.GeneratedAssetType === "Instapost") {
                        chipColor = theme.palette.error.main;
                        chipBg = alpha(theme.palette.error.main, 0.1);
                      }

                      return (
                        <TableRow key={gen.s_no} sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                          <TableCell align="center" sx={{ color: "#64748b" }}>{gen.s_no}</TableCell>
                          <TableCell sx={{ fontWeight: 600, color: "#1e293b" }}>{gen.publicationTitle}</TableCell>
                          <TableCell>
                            <Chip
                              label={gen.GeneratedAssetType}
                              size="small"
                              sx={{
                                bgcolor: chipBg,
                                color: chipColor,
                                fontWeight: 700,
                                fontSize: "0.75rem"
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ color: "#64748b" }}>{gen.purpose}</TableCell>
                          <TableCell sx={{ color: "#64748b" }}>
                            {gen.date && gen.date !== "N/A" ? (
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: "#1e293b" }}>{gen.date.split(" ")[0]}</Typography>
                                {gen.date.split(" ")[1] && <Typography variant="caption" sx={{ color: "#94a3b8" }}>{gen.date.split(" ")[1]}</Typography>}
                              </Box>
                            ) : "N/A"}
                          </TableCell>
                          <TableCell align="right" sx={{ color: "#334155" }}>{gen.creditsUsed.toFixed(1)}</TableCell>
                          <TableCell align="right" sx={{ color: "#334155" }}>{gen.Regenerations.toFixed(1)}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, color: "#1e4de3" }}>{gen.total.toFixed(1)}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" height={250} gap={1} p={4}>
                <Typography variant="body1" sx={{ fontWeight: 600, color: "#475569" }}>No generations recorded</Typography>
                <Typography variant="body2" sx={{ color: "#94a3b8" }}>This user hasn't generated any assets yet.</Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2.5, borderTop: "1px solid #f1f5f9" }}>
            <Button
              onClick={() => setDialogOpen(false)}
              variant="contained"
              disableElevation
              sx={{
                bgcolor: "#1e4de3",
                borderRadius: 2,
                px: 3,
                textTransform: "none",
                fontWeight: 600,
                "&:hover": { bgcolor: "#1138b3" }
              }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </SidebarTopbar>
  );
}
